import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const ACCESS_KEY = "gms.access";
const REFRESH_KEY = "gms.refresh";
const BRANCH_KEY = "gms.current_branch_id";

// ------------------ token storage ------------------
export const tokens = {
    getAccess() {
        return localStorage.getItem(ACCESS_KEY);
    },
    getRefresh() {
        return localStorage.getItem(REFRESH_KEY);
    },
    set({ access_token, refresh_token }) {
        if (access_token) localStorage.setItem(ACCESS_KEY, access_token);
        if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
    },
    clear() {
        localStorage.removeItem(ACCESS_KEY);
        localStorage.removeItem(REFRESH_KEY);
    },
};

export const currentBranch = {
    get() {
        return localStorage.getItem(BRANCH_KEY);
    },
    set(id) {
        if (id) localStorage.setItem(BRANCH_KEY, id);
        else localStorage.removeItem(BRANCH_KEY);
    },
};

// ------------------ axios client w/ refresh-on-401 ------------------
export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
    const t = tokens.getAccess();
    if (t) config.headers.Authorization = `Bearer ${t}`;
    const b = currentBranch.get();
    if (b) config.headers["X-Branch-Id"] = b;
    return config;
});

let refreshing = null;

api.interceptors.response.use(
    (resp) => resp,
    async (err) => {
        const original = err.config;
        if (!original) throw err;
        const status = err.response && err.response.status;
        if (status !== 401 || original.__retried) throw err;
        // public auth endpoints
        if (original.url && original.url.match(/\/auth\/(login|register-tenant|refresh)/)) {
            throw err;
        }
        if (!tokens.getRefresh()) {
            tokens.clear();
            throw err;
        }
        original.__retried = true;
        try {
            if (!refreshing) {
                refreshing = axios
                    .post(`${API_BASE}/auth/refresh`, { refresh_token: tokens.getRefresh() })
                    .then((r) => {
                        tokens.set(r.data);
                        refreshing = null;
                        return r.data.access_token;
                    })
                    .catch((e) => {
                        refreshing = null;
                        tokens.clear();
                        throw e;
                    });
            }
            const newAccess = await refreshing;
            original.headers.Authorization = `Bearer ${newAccess}`;
            return api(original);
        } catch (e) {
            tokens.clear();
            // hard-redirect to login if we lose auth
            if (window.location.pathname !== "/login") {
                window.location.assign("/login");
            }
            throw e;
        }
    }
);

// ------------------ typed helpers ------------------
export const AuthAPI = {
    login: (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
    registerTenant: (body) => api.post("/auth/register-tenant", body).then((r) => r.data),
    me: () => api.get("/auth/me").then((r) => r.data),
    logout: () => api.post("/auth/logout", { refresh_token: tokens.getRefresh() }).then((r) => r.data),
};

export const TenantAPI = {
    get: () => api.get("/tenants/me").then((r) => r.data),
    update: (body) => api.patch("/tenants/me", body).then((r) => r.data),
    getSettings: () => api.get("/tenants/me/settings").then((r) => r.data),
    updateSettings: (body) => api.patch("/tenants/me/settings", body).then((r) => r.data),
    listRoles: () => api.get("/tenants/me/roles").then((r) => r.data),
    dataExport: () => api.post("/tenants/me/data-export").then((r) => r.data),
};

export const BranchAPI = {
    list: () => api.get("/branches").then((r) => r.data),
    create: (body) => api.post("/branches", body).then((r) => r.data),
    update: (id, body) => api.patch(`/branches/${id}`, body).then((r) => r.data),
    remove: (id) => api.delete(`/branches/${id}`).then((r) => r.data),
};

export const UserAPI = {
    list: () => api.get("/users").then((r) => r.data),
    create: (body) => api.post("/users", body).then((r) => r.data),
    update: (id, body) => api.patch(`/users/${id}`, body).then((r) => r.data),
    deactivate: (id) => api.patch(`/users/${id}/deactivate`).then((r) => r.data),
    activate: (id) => api.patch(`/users/${id}/activate`).then((r) => r.data),
};

export const AuditAPI = {
    list: (params = {}) => api.get("/audit-logs", { params }).then((r) => r.data),
};

export const CustomerAPI = {
    list: (params = {}) => api.get("/customers", { params }).then((r) => r.data),
    create: (body) => api.post("/customers", body).then((r) => r.data),
    get: (id) => api.get(`/customers/${id}`).then((r) => r.data),
    update: (id, body) => api.patch(`/customers/${id}`, body).then((r) => r.data),
    remove: (id) => api.delete(`/customers/${id}`).then((r) => r.data),
};

export const VehicleAPI = {
    list: (params = {}) => api.get("/vehicles", { params }).then((r) => r.data),
    create: (body) => api.post("/vehicles", body).then((r) => r.data),
    get: (id) => api.get(`/vehicles/${id}`).then((r) => r.data),
    update: (id, body) => api.patch(`/vehicles/${id}`, body).then((r) => r.data),
    remove: (id) => api.delete(`/vehicles/${id}`).then((r) => r.data),
    addOdometer: (id, reading) => api.post(`/vehicles/${id}/odometer`, { reading }).then((r) => r.data),
    history: (id) => api.get(`/vehicles/${id}/history`).then((r) => r.data),
};

export const RegistryAPI = {
    lookup: (registration_number, provider = "auto") =>
        api.get("/registry/lookup", { params: { registration_number, provider } }).then((r) => r.data),
};

export const HealthAPI = {
    get: () => api.get("/health").then((r) => r.data),
};

// ---------- Phase 3 ----------

export const BayAPI = {
    list: (params = {}) => api.get("/bays", { params }).then((r) => r.data),
    create: (body) => api.post("/bays", body).then((r) => r.data),
    get: (id) => api.get(`/bays/${id}`).then((r) => r.data),
    update: (id, body) => api.patch(`/bays/${id}`, body).then((r) => r.data),
    remove: (id) => api.delete(`/bays/${id}`).then((r) => r.data),
};

export const SkillAPI = {
    list: (params = {}) => api.get("/skills", { params }).then((r) => r.data),
    create: (body) => api.post("/skills", body).then((r) => r.data),
    update: (id, body) => api.patch(`/skills/${id}`, body).then((r) => r.data),
};

export const TechnicianAPI = {
    list: (params = {}) => api.get("/technicians", { params }).then((r) => r.data),
    create: (body) => api.post("/technicians", body).then((r) => r.data),
    get: (id) => api.get(`/technicians/${id}`).then((r) => r.data),
    update: (id, body) => api.patch(`/technicians/${id}`, body).then((r) => r.data),
    remove: (id) => api.delete(`/technicians/${id}`).then((r) => r.data),
    addCertification: (id, body) =>
        api.post(`/technicians/${id}/certifications`, body).then((r) => r.data),
};

export const AppointmentAPI = {
    list: (params = {}) => api.get("/appointments", { params }).then((r) => r.data),
    create: (body) => api.post("/appointments", body).then((r) => r.data),
    get: (id) => api.get(`/appointments/${id}`).then((r) => r.data),
    update: (id, body) => api.patch(`/appointments/${id}`, body).then((r) => r.data),
    reschedule: (id, body) => api.post(`/appointments/${id}/reschedule`, body).then((r) => r.data),
    cancel: (id, body) => api.post(`/appointments/${id}/cancel`, body).then((r) => r.data),
    checkIn: (id, body) => api.post(`/appointments/${id}/check-in`, body).then((r) => r.data),
    convertToJob: (id, { force = false } = {}) =>
        api.post(`/appointments/${id}/convert-to-job`, null, { params: force ? { force: true } : {} }).then((r) => r.data),
    sendReminder: (id) => api.post(`/appointments/${id}/send-reminder`).then((r) => r.data),
};

export const JobCardAPI = {
    list: (params = {}) => api.get("/jobcards", { params }).then((r) => r.data),
    counts: (params = {}) => api.get("/jobcards/counts", { params }).then((r) => r.data),
    create: (body) => api.post("/jobcards", body).then((r) => r.data),
    get: (id) => api.get(`/jobcards/${id}`).then((r) => r.data),
    update: (id, body) => api.patch(`/jobcards/${id}`, body).then((r) => r.data),
    transition: (id, body) => api.post(`/jobcards/${id}/transition`, body).then((r) => r.data),
    markApproved: (id, body) => api.post(`/jobcards/${id}/mark-approved`, body).then((r) => r.data),
    tat: (id) => api.get(`/jobcards/${id}/tat`).then((r) => r.data),
    startTimer: (id, body) => api.post(`/jobcards/${id}/time-log/start`, body).then((r) => r.data),
    stopTimer: (id, body) => api.post(`/jobcards/${id}/time-log/stop`, body).then((r) => r.data),
    timeLogs: (id) => api.get(`/jobcards/${id}/time-logs`).then((r) => r.data),
};

// ---------- Phase 4 ----------

export const AttachmentAPI = {
    list: (jobId) => api.get(`/jobcards/${jobId}/attachments`).then((r) => r.data),
    upload: (jobId, files, perFileMeta = []) => {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        perFileMeta.forEach((m) => {
            fd.append("kinds", m.kind || "other");
            fd.append("captions", m.caption || "");
        });
        return api.post(`/jobcards/${jobId}/attachments`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        }).then((r) => r.data);
    },
    update: (jobId, attId, body) =>
        api.patch(`/jobcards/${jobId}/attachments/${attId}`, body).then((r) => r.data),
    remove: (jobId, attId) =>
        api.delete(`/jobcards/${jobId}/attachments/${attId}`).then((r) => r.data),
};

export const NotificationAPI = {
    list: (params = {}) => api.get("/notifications", { params }).then((r) => r.data),
};

// ---------- Phase 5: Inventory / Parts / Suppliers / Purchase Orders ----------

export const SupplierAPI = {
    list: (params = {}) => api.get("/suppliers", { params }).then((r) => r.data),
    create: (body) => api.post("/suppliers", body).then((r) => r.data),
    get: (id) => api.get(`/suppliers/${id}`).then((r) => r.data),
    update: (id, body) => api.patch(`/suppliers/${id}`, body).then((r) => r.data),
    remove: (id) => api.delete(`/suppliers/${id}`).then((r) => r.data),
};

export const PartAPI = {
    list: (params = {}) => api.get("/parts", { params }).then((r) => r.data),
    create: (body) => api.post("/parts", body).then((r) => r.data),
    get: (id) => api.get(`/parts/${id}`).then((r) => r.data),
    update: (id, body) => api.patch(`/parts/${id}`, body).then((r) => r.data),
    remove: (id) => api.delete(`/parts/${id}`).then((r) => r.data),
    movements: (id, params = {}) =>
        api.get(`/parts/${id}/movements`, { params }).then((r) => r.data),
    adjust: (id, body) => api.post(`/parts/${id}/adjust`, body).then((r) => r.data),
    setThreshold: (id, body) =>
        api.post(`/parts/${id}/threshold-override`, body).then((r) => r.data),
};

export const StockAPI = {
    branchStock: (branchId, params = {}) =>
        api.get(`/branches/${branchId}/stock`, { params }).then((r) => r.data),
    lowStockAlerts: (params = {}) =>
        api.get("/alerts/low-stock", { params }).then((r) => r.data),
};

export const PurchaseOrderAPI = {
    list: (params = {}) => api.get("/purchase-orders", { params }).then((r) => r.data),
    create: (body) => api.post("/purchase-orders", body).then((r) => r.data),
    get: (id) => api.get(`/purchase-orders/${id}`).then((r) => r.data),
    update: (id, body) => api.patch(`/purchase-orders/${id}`, body).then((r) => r.data),
    remove: (id) => api.delete(`/purchase-orders/${id}`).then((r) => r.data),
    addLine: (id, body) => api.post(`/purchase-orders/${id}/lines`, body).then((r) => r.data),
    patchLine: (id, lineId, body) =>
        api.patch(`/purchase-orders/${id}/lines/${lineId}`, body).then((r) => r.data),
    deleteLine: (id, lineId) =>
        api.delete(`/purchase-orders/${id}/lines/${lineId}`).then((r) => r.data),
    send: (id) => api.post(`/purchase-orders/${id}/send`).then((r) => r.data),
    cancel: (id) => api.post(`/purchase-orders/${id}/cancel`).then((r) => r.data),
    receive: (id, body) => api.post(`/purchase-orders/${id}/receive`, body).then((r) => r.data),
};

export const JobPartAPI = {
    list: (jobId) => api.get(`/jobcards/${jobId}/parts`).then((r) => r.data),
    consume: (jobId, body) => api.post(`/jobcards/${jobId}/parts`, body).then((r) => r.data),
    returnLine: (jobId, lineId, body) =>
        api.post(`/jobcards/${jobId}/parts/${lineId}/return`, body).then((r) => r.data),
};

// ---------- Phase 6: Estimates / Invoices / Payments ----------

export const EstimateAPI = {
    list: (params = {}) => api.get("/estimates", { params }).then((r) => r.data),
    create: (body) => api.post("/estimates", body).then((r) => r.data),
    get: (id) => api.get(`/estimates/${id}`).then((r) => r.data),
    sendForApproval: (id) => api.post(`/estimates/${id}/send-for-approval`).then((r) => r.data),
    convertToInvoice: (id) => api.post(`/estimates/${id}/convert-to-invoice`).then((r) => r.data),
};

export const InvoiceAPI = {
    list: (params = {}) => api.get("/invoices", { params }).then((r) => r.data),
    summary: (params = {}) => api.get("/invoices/summary", { params }).then((r) => r.data),
    create: (body) => api.post("/invoices", body).then((r) => r.data),
    get: (id) => api.get(`/invoices/${id}`).then((r) => r.data),
    issue: (id) => api.post(`/invoices/${id}/issue`).then((r) => r.data),
    generateEinvoice: (id) => api.post(`/invoices/${id}/generate-einvoice`).then((r) => r.data),
    cancel: (id, reason) => api.post(`/invoices/${id}/cancel`, { reason }).then((r) => r.data),
    creditNote: (id, reason) => api.post(`/invoices/${id}/credit-note`, { reason }).then((r) => r.data),
    pdfUrl: (id) => {
        const root = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
        return `${root}/api/invoices/${id}/pdf`;
    },
    // Authenticated PDF fetch — the raw /pdf endpoint requires a Bearer token,
    // which a plain <a href>/<iframe src> navigation cannot supply (→ 401).
    // These helpers fetch the PDF as a blob with the access token attached.
    pdfBlobUrl: async (id) => {
        const r = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
        return window.URL.createObjectURL(r.data);
    },
    downloadPdf: async (id, filename = `invoice-${id}.pdf`) => {
        const r = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
        const url = window.URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        return true;
    },
};

export const PaymentAPI = {
    list: (params = {}) => api.get("/payments", { params }).then((r) => r.data),
    create: (body) => api.post("/payments", body).then((r) => r.data),
    get: (id) => api.get(`/payments/${id}`).then((r) => r.data),
    gatewayInit: (body) => api.post("/payments/gateway/init", body).then((r) => r.data),
    gatewayWebhook: (provider, body, { headers = {} } = {}) => {
        // Unauth — stub mode only. Used by GatewayCheckoutButton to simulate
        // the provider POSTing the capture back to us synchronously.
        const root = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
        return fetch(`${root}/api/payments/gateway/webhook/${provider}`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-Provider-Signature": "ok", ...headers },
            body: JSON.stringify(body),
        }).then(async (r) => ({ status: r.status, body: await r.json().catch(() => ({})) }));
    },
    refund: (id, body) => api.post(`/payments/${id}/refund`, body).then((r) => r.data),
};

export const PublicEstimateAPI = {
    get: (token) => {
        const root = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
        return fetch(`${root}/api/public/estimates/${token}`).then((r) => r.json());
    },
    respond: (token, body) => {
        const root = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
        return fetch(`${root}/api/public/estimates/${token}/respond`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        }).then((r) => r.json());
    },
};

// =================================================================
// Phase 7 — Technical Data + DTC
// =================================================================
export const TechDataAPI = {
    list: (params = {}) => api.get("/tech-data", { params }).then((r) => r.data),
    get: (id) => api.get(`/tech-data/${id}`).then((r) => r.data),
    create: (body) => api.post("/tech-data", body).then((r) => r.data),
    update: (id, patch) => api.patch(`/tech-data/${id}`, patch).then((r) => r.data),
    remove: (id) => api.delete(`/tech-data/${id}`).then((r) => r.data),
    lookup: (vehicle_id) => api.post("/tech-data/lookup", { vehicle_id }).then((r) => r.data),
};

export const DtcAPI = {
    catalogue: (params = {}) => api.get("/dtc/catalogue", { params }).then((r) => r.data),
    decode: (code) => api.get(`/dtc/catalogue/${encodeURIComponent(code)}`).then((r) => r.data),
    listForJob: (jobId) => api.get(`/jobcards/${jobId}/dtc`).then((r) => r.data),
    ingestForJob: (jobId, body) => api.post(`/jobcards/${jobId}/dtc`, body).then((r) => r.data),
    clearIncident: (jobId, incId, body) =>
        api.post(`/jobcards/${jobId}/dtc/${incId}/clear`, body).then((r) => r.data),
    vehicleHistory: (vehicleId) =>
        api.get(`/vehicles/${vehicleId}/dtc-history`).then((r) => r.data),
};


// =================================================================
// Phase 8.1 — Digital Vehicle Inspection
// =================================================================
// =================================================================
// Phase 8.2 — Public DVI customer-approval page (NO auth)
// =================================================================
const PUBLIC_API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

export const PublicInspectionAPI = {
    async get(token) {
        const r = await fetch(`${PUBLIC_API_BASE}/public/inspections/${encodeURIComponent(token)}`,
                                { headers: { Accept: "application/json" }, cache: "no-store" });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
            const code = (data && data.error && data.error.code) || `HTTP_${r.status}`;
            const err = new Error((data && data.error && data.error.message) || "Network error");
            err.code = code;
            err.status = r.status;
            throw err;
        }
        return data;
    },
    async submitDecisions(token, payload) {
        const r = await fetch(`${PUBLIC_API_BASE}/public/inspections/${encodeURIComponent(token)}/decisions`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            cache: "no-store",
            body: JSON.stringify(payload),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
            const code = (data && data.error && data.error.code) || `HTTP_${r.status}`;
            const err = new Error((data && data.error && data.error.message) || "Network error");
            err.code = code;
            err.status = r.status;
            throw err;
        }
        return data;
    },
    pdfUrl(token) {
        return `${PUBLIC_API_BASE}/public/inspections/${encodeURIComponent(token)}/pdf`;
    },
};

export const DashboardAPI = {
    dviUpsellToday: (params = {}) =>
        api.get("/dashboard/dvi-upsell-today", { params }).then((r) => r.data),
    dviUpsellTodayFindings: () =>
        api.get("/dashboard/dvi-upsell-today/findings").then((r) => r.data),
    remindersSummary: () =>
        api.get("/dashboard/reminders-summary").then((r) => r.data),
    bi: (params = {}) =>
        api.get("/dashboard/bi", { params }).then((r) => r.data),
};

// Phase 8.3 — Service reminders (tenant-scoped, gated on reminder.read/write)
export const ReminderAPI = {
    list: (params = {}) => api.get("/reminders", { params }).then((r) => r.data),
    get: (id) => api.get(`/reminders/${id}`).then((r) => r.data),
    create: (body) => api.post("/reminders", body).then((r) => r.data),
    patch: (id, body) => api.patch(`/reminders/${id}`, body).then((r) => r.data),
    snooze: (id, snoozeUntilIso) =>
        api.post(`/reminders/${id}/snooze`, { snooze_until: snoozeUntilIso })
            .then((r) => r.data),
    cancel: (id, reason) =>
        api.post(`/reminders/${id}/cancel`, { reason: reason || null })
            .then((r) => r.data),
};

// Phase 8.3 — Admin-only notification outbox viewer
export const AdminOutboxAPI = {
    list: (params = {}) =>
        api.get("/admin/outbox", { params }).then((r) => r.data),
    get: (id) => api.get(`/admin/outbox/${id}`).then((r) => r.data),
};

export const DviAPI = {
    // Templates
    listTemplates: (params = {}) =>
        api.get("/dvi/templates", { params }).then((r) => r.data),
    getTemplate: (id) => api.get(`/dvi/templates/${id}`).then((r) => r.data),

    // Inspections — per jobcard
    listForJobcard: (jobId) =>
        api.get(`/jobcards/${jobId}/inspections`).then((r) => r.data),
    startForJobcard: (jobId, body = {}) =>
        api.post(`/jobcards/${jobId}/inspections`, body).then((r) => r.data),

    // Inspections — tenant-wide
    listInspections: (params = {}) =>
        api.get("/dvi/inspections", { params }).then((r) => r.data),
    getInspection: (id) =>
        api.get(`/dvi/inspections/${id}`).then((r) => r.data),
    patchInspection: (id, body) =>
        api.patch(`/dvi/inspections/${id}`, body).then((r) => r.data),
    issueShare: (id) =>
        api.post(`/dvi/inspections/${id}/share`).then((r) => r.data),
    revokeShare: (id) =>
        api.post(`/dvi/inspections/${id}/revoke-share`).then((r) => r.data),
    pdfUrl: (id) => {
        const root = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
        return `${root}/api/dvi/inspections/${id}/pdf`;
    },

    // Findings
    upsertFinding: (inspectionId, body) =>
        api.post(`/dvi/inspections/${inspectionId}/findings`, body)
            .then((r) => r.data),
    patchFinding: (findingId, body) =>
        api.patch(`/dvi/findings/${findingId}`, body).then((r) => r.data),
    addFindingAttachments: (findingId, body) =>
        api.post(`/dvi/findings/${findingId}/attachments`, body)
            .then((r) => r.data),
    convertFindingToEstimate: (findingId) =>
        api.post(`/dvi/findings/${findingId}/convert-to-estimate-line`, {})
            .then((r) => r.data),
};


// Build a fully-qualified URL for a backend-provided signed-URL path.
export function buildSignedUrl(path) {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    return `${BACKEND_URL}${path}`;
}

// Unauth public endpoint
export const PublicAPI = {
    job: (token) => axios.get(`${API_BASE}/public/jobs/${token}`).then((r) => r.data),
};

// =================================================================
// Phase 8.0b — Platform Admin (cross-tenant). All gated by
// require_platform_admin on the server side.
// =================================================================
export const AdminAPI = {
    metrics: () => api.get("/admin/metrics").then((r) => r.data),
    listTenants: (params = {}) => api.get("/admin/tenants", { params }).then((r) => r.data),
    getTenant: (id) => api.get(`/admin/tenants/${id}`).then((r) => r.data),
    createTenant: (body) => api.post("/admin/tenants", body).then((r) => r.data),
    updateTenant: (id, body) => api.patch(`/admin/tenants/${id}`, body).then((r) => r.data),
    listUsers: (params = {}) => api.get("/admin/users", { params }).then((r) => r.data),
    listAuditLog: (params = {}) => api.get("/admin/audit-log", { params }).then((r) => r.data),
    impersonate: (tenantId) =>
        api.post(`/admin/impersonate/${tenantId}`).then((r) => r.data),
    endImpersonation: () => api.post("/admin/impersonate/end").then((r) => r.data),
};

// Convert error responses to user-friendly messages. Returns "CODE: message"
// where a backend error code is available so E2E specs can string-match on the
// stable code (e.g. UNSUPPORTED_MEDIA_TYPE, FILE_TOO_LARGE) without depending
// on the human-readable copy.
export function extractError(err) {
    const r = err && err.response;
    if (r && r.data && r.data.error) {
        const e = r.data.error;
        const msg = e.message || "Something went wrong";
        return e.code ? `${e.code}: ${msg}` : msg;
    }
    return (err && err.message) || "Network error";
}
